import { Check, X } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  humanisePipelineStageName,
  pipelineStageI18nKey,
  type PipelineStage,
} from '~/features/experiments/services/experiment-pipeline.service';
import { cn } from '~/utils/cn';
import { formatDurationBetween, formatRelative } from '~/utils/date';

interface Props {
  /**
   * Live per-stage progress from `GET /pipeline/status`. The timeline is
   * driven directly by this array — its order and contents are the source
   * of truth, so new backend stages appear automatically without a
   * frontend change. Callers should withhold rendering when the array is
   * empty (no stages stamped yet) and show their own preparing state.
   */
  stages: PipelineStage[];
  className?: string;
  /** Show the "In Progress" badge above the active node. Off by default (Full Catalog page). */
  showInProgressBadge?: boolean;
  /** Show the per-stage product-count chip below the node. Off by default. */
  showCount?: boolean;
  /** Override a stage's display label by backend name (e.g. `{ Delivery: 'Experiment Strategy' }`). */
  labelOverrides?: Record<string, string>;
  /** `data-testid` root; each stage gets `${testId}-stage-{stageName}` (+ `-label`, `-meta`, `-count`). */
  testId?: string;
}

/**
 * Renders the caller-supplied `stages` as a horizontal numbered stepper: a
 * circle node per stage joined by connector lines, with the stage label, timing
 * meta (live "started X ago" while running, a fixed run duration once terminal),
 * and optional product-count chip stacked beneath each node. The stage set is
 * whatever the caller passes — the content-enrichment "Full Catalog Generation"
 * page passes its generation stages (Curation → Enrichment → PostScoring), while
 * the Delivery page passes the full pipeline.
 *
 * Status → visual map:
 *   completed   → green circle + check, green connector into the next node
 *   in_progress → primary circle + step number, "In Progress" badge above
 *   awaiting    → muted circle + step number (parked, treated as not-yet-reached)
 *   failed      → destructive circle + ✕ (system error)
 *   rejected    → destructive circle + ✕ (operator decision — BUG-029)
 *   skipped     → muted dashed circle + step number (stage bypassed, e.g. Delivery after rejection)
 *   pending     → muted circle + step number (default for stages not yet stamped)
 *
 * Pipeline-domain i18n keys live under `experiments:detail.launch.stages.*` so
 * any caller renders the same labels — passing a different namespace's `t` would
 * silently miss strings.
 */
export function PipelineStageTimeline({
  stages,
  className,
  showInProgressBadge = false,
  showCount = false,
  labelOverrides = {},
  testId = 'pipeline-stage-timeline',
}: Props) {
  const { t } = useTranslation('experiments');
  return (
    // Absolute label/badge layers don't add to flow height, so pad the wrapper
    // to reserve room for the badge above and the label stack below each node.
    <div data-testid={testId} className={cn('w-full px-8 pt-10 pb-24', className)}>
      <ol className="flex items-center">
        {stages.map((stage, index) => {
          const status = stage.status;
          // Connector is "done" (green) only once the stage to its left completed.
          const connectorDone = index > 0 && stages[index - 1]?.status === 'completed';
          const meta = renderStageMeta(status, stage, t);
          return (
            <Fragment key={stage.name}>
              {index > 0 && (
                <li
                  aria-hidden="true"
                  className={cn('h-0.5 flex-1', connectorDone && 'bg-success')}
                  style={
                    connectorDone ? undefined : { backgroundColor: 'var(--surface-progress-track)' }
                  }
                />
              )}
              <li
                data-testid={`${testId}-stage-${stage.name}`}
                data-status={status}
                className="relative flex w-12 shrink-0 flex-col items-center"
              >
                {showInProgressBadge && status === 'in_progress' && (
                  <span
                    data-testid={`${testId}-stage-${stage.name}-in-progress-badge`}
                    className="bg-info-50 text-info absolute bottom-full mb-3 rounded-md px-3 py-1 text-xs font-medium whitespace-nowrap"
                  >
                    {t('detail.launch.stages.inProgress')}
                  </span>
                )}
                {renderStageNode(status, index + 1)}
                <div className="absolute top-full left-1/2 mt-3 flex w-max -translate-x-1/2 flex-col items-center">
                  <span
                    data-testid={`${testId}-stage-${stage.name}-label`}
                    className={cn('text-sm font-semibold whitespace-nowrap', labelColorFor(status))}
                  >
                    {labelOverrides[stage.name] ??
                      t(`detail.launch.stages.${pipelineStageI18nKey(stage.name)}`, {
                        defaultValue: humanisePipelineStageName(stage.name),
                      })}
                  </span>
                  {meta && (
                    <span
                      data-testid={`${testId}-stage-${stage.name}-meta`}
                      className="text-muted-foreground mt-1 text-xs whitespace-nowrap"
                    >
                      {meta}
                    </span>
                  )}
                  {showCount && typeof stage.count === 'number' && (
                    <span
                      data-testid={`${testId}-stage-${stage.name}-count`}
                      className="text-muted-foreground bg-muted mt-2 rounded-full px-3 py-1 text-xs whitespace-nowrap"
                    >
                      {t('detail.launch.stages.count', { count: stage.count })}
                    </span>
                  )}
                </div>
              </li>
            </Fragment>
          );
        })}
      </ol>
    </div>
  );
}

/** Renders the circular step node: check/✕ icon for terminal states, step number otherwise. */
function renderStageNode(status: string, step: number): ReactNode {
  const base =
    'flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-base font-semibold';
  switch (status) {
    case 'completed':
      return (
        <span className={cn(base, 'bg-success text-white')}>
          <Check aria-hidden="true" className="h-6 w-6" strokeWidth={3} />
        </span>
      );
    case 'failed':
    case 'rejected': // operator declined the approval gate — same destructive visual as failed
      return (
        <span className={cn(base, 'bg-destructive text-white')}>
          <X aria-hidden="true" className="h-6 w-6" strokeWidth={3} />
        </span>
      );
    case 'in_progress':
      return <span className={cn(base, 'bg-primary text-primary-foreground')}>{step}</span>;
    // skipped = stage was intentionally bypassed (e.g. Delivery after a rejection).
    // Dashed border distinguishes it from pending (not-yet-reached) and awaiting (parked).
    case 'skipped':
      return (
        <span
          className={cn(base, 'text-muted-foreground border-2 border-dashed')}
          style={{
            borderColor: 'var(--surface-progress-track)',
            backgroundColor: 'transparent',
          }}
        >
          {step}
        </span>
      );
    case 'awaiting':
    default:
      return (
        <span
          className={cn(base, 'text-muted-foreground')}
          style={{ backgroundColor: 'var(--surface-progress-track)' }}
        >
          {step}
        </span>
      );
  }
}

/** Stage-label text color keyed off status — primary for active, muted for not-yet-reached. */
function labelColorFor(status: string): string {
  switch (status) {
    case 'in_progress':
      return 'text-primary';
    case 'failed':
    case 'rejected':
      return 'text-destructive';
    case 'awaiting':
    case 'pending':
    case 'skipped':
      return 'text-muted-foreground';
    default:
      return 'text-foreground';
  }
}

type Translate = ReturnType<typeof useTranslation>['t'];

/**
 * Sub-label under each node. A running stage shows a live "started X ago"; every terminal stage
 * (completed/failed/rejected) shows how long the stage took — a fixed startedAt→completedAt
 * duration that never ticks, since a finished stage's elapsed time is a measurement, not a clock.
 */
function renderStageMeta(status: string, stage: PipelineStage, t: Translate): string {
  // In-progress is the only live value: how long ago the stage started.
  if (status === 'in_progress' && stage.startedAt) {
    return formatRelative(stage.startedAt);
  }
  // Terminal stages show a frozen run duration; fall back to the status label when either
  // timestamp is missing so the value still never changes once displayed.
  if (status === 'completed' || status === 'failed' || status === 'rejected') {
    const duration =
      stage.startedAt && stage.completedAt
        ? formatDurationBetween(stage.startedAt, stage.completedAt)
        : '';
    return duration || t(`detail.launch.stages.${status}`);
  }
  // skipped = stage was never executed (e.g. Delivery after a rejection); no timing.
  if (status === 'skipped') {
    return t('detail.launch.stages.skipped');
  }
  // pending / scheduled / awaiting (and any unknown wire status) carry no timing meta.
  return '';
}
